// Premium SVG cumulative-savings chart for the system's life horizon.
// Phase 2 refresh: larger, clearer labels — easier to read break-even, final 15-yr
// value and the installed-cost reference line — while keeping AeroMax navy/green.
//
// Props:
//  - yearly: [{ year, yearSaving, cumulative }] (year 1..N from the engine)
//  - installedCost: number (used for the dashed reference line and the
//    break-even marker — Phase 2 uses $6,999 as the ROI assumption)
export default function SavingsChart({ yearly = [], installedCost = 6999 }) {
  if (!yearly.length) return null;

  // Build a series starting at Year 0 with cumulative = 0
  const points = [
    { year: 0, cumulative: 0 },
    ...yearly.map((p) => ({ year: p.year, cumulative: p.cumulative })),
  ];
  const lastYear = points[points.length - 1].year;
  const finalVal = points[points.length - 1].cumulative;
  const maxVal = Math.max(finalVal, installedCost * 1.15);

  // Larger canvas with generous padding so labels never crash into the chart
  const w = 720;
  const h = 320;
  const padX = 64;
  const padTop = 36;
  const padBottom = 48;

  const xFor = (y) => padX + (y / lastYear) * (w - padX * 2);
  const yFor = (v) => padTop + (1 - v / maxVal) * (h - padTop - padBottom);

  // Cumulative path
  const cumPath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(p.year)} ${yFor(p.cumulative)}`)
    .join(" ");
  // Soft base area under the whole curve
  const areaPath = `${cumPath} L ${xFor(lastYear)} ${yFor(0)} L ${xFor(0)} ${yFor(0)} Z`;

  // Break-even (where cumulative crosses installedCost)
  let beX = null;
  let beYear = null;
  for (let i = 1; i < points.length; i++) {
    if (
      points[i].cumulative >= installedCost &&
      points[i - 1].cumulative < installedCost
    ) {
      const prev = points[i - 1];
      const cur = points[i];
      const t = (installedCost - prev.cumulative) / (cur.cumulative - prev.cumulative);
      const yr = prev.year + t * (cur.year - prev.year);
      beX = xFor(yr);
      beYear = Math.ceil(yr);
      break;
    }
  }
  const beVisible = beX !== null;
  const beY = yFor(installedCost);

  // Profit wedge — visible from break-even onward, between installed-cost line and cumulative curve
  let profitPath = "";
  if (beVisible) {
    const seg = points.filter((p) => p.year >= beYear - 1);
    profitPath = `M ${beX} ${beY}`;
    seg.forEach((p) => {
      profitPath += ` L ${xFor(p.year)} ${yFor(p.cumulative)}`;
    });
    profitPath += ` L ${xFor(lastYear)} ${beY} L ${beX} ${beY} Z`;
  }

  // X-axis labels — meaningful "Year 0/3/6/9/12/15"
  const tickYears = [0, 3, 6, 9, 12, 15].filter((y) => y <= lastYear);

  // Y-axis ticks at 0 / 50% / 100% of maxVal
  const yTicks = [0, 0.5, 1].map((g) => {
    const v = g * maxVal;
    return { v, y: yFor(v) };
  });

  // Final-year label positioning — keep it inside the canvas
  const finalLabelText = `${lastYear}-yr · $${Math.round(finalVal).toLocaleString()}`;
  const finalLabelW = Math.min(220, finalLabelText.length * 8 + 20);
  const finalLabelX = Math.max(padX, xFor(lastYear) - finalLabelW - 6);
  const finalLabelY = Math.max(padTop + 4, yFor(finalVal) - 30);

  // Break-even label positioning — anchor right of the marker, clamp inside canvas
  const beLabelText = beVisible ? `Break-even ≈ Year ${beYear}` : "";
  const beLabelW = beLabelText.length * 7.2 + 20;
  const beLabelX = beVisible
    ? Math.min(w - padX - beLabelW, beX + 12)
    : 0;
  const beLabelY = Math.min(h - padBottom - 28, beY + 10);

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Cumulative savings over 15 years"
      >
        <defs>
          <linearGradient id="cumBaseGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0.18" />
          </linearGradient>
        </defs>

        {/* Y-axis $ labels (left margin) */}
        {yTicks.map((t, i) => (
          <text
            key={i}
            x={padX - 10}
            y={t.y + 5}
            textAnchor="end"
            fontSize="13"
            fill="#475569"
            fontWeight="700"
          >
            ${Math.round(t.v).toLocaleString()}
          </text>
        ))}

        {/* Gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((g, i) => (
          <line
            key={i}
            x1={padX}
            x2={w - padX}
            y1={padTop + g * (h - padTop - padBottom)}
            y2={padTop + g * (h - padTop - padBottom)}
            stroke="#E2E8F0"
            strokeWidth="1"
            strokeDasharray="3 4"
          />
        ))}

        {/* Soft base area under the whole curve */}
        <path d={areaPath} fill="url(#cumBaseGrad)" />

        {/* Stronger green wedge after break-even — visualises the net-savings zone */}
        {beVisible && <path d={profitPath} fill="url(#profitGrad)" />}

        {/* Installed-cost reference line — thicker, clearer label */}
        <line
          x1={padX}
          x2={w - padX}
          y1={beY}
          y2={beY}
          stroke="#0B132B"
          strokeOpacity="0.55"
          strokeWidth="2"
          strokeDasharray="6 5"
        />
        <rect
          x={padX}
          y={beY - 24}
          rx="4"
          width={`${`Installed cost · $${installedCost.toLocaleString()}`.length * 7.4 + 14}`}
          height="20"
          fill="#0B132B"
        />
        <text
          x={padX + 7}
          y={beY - 9}
          fontSize="12"
          fill="white"
          fontWeight="700"
          letterSpacing="0.02em"
        >
          {`Installed cost · $${installedCost.toLocaleString()}`}
        </text>

        {/* Cumulative savings line — thicker and more noticeable */}
        <path
          d={cumPath}
          fill="none"
          stroke="#10B981"
          strokeWidth="4.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Break-even marker — bigger circle + clearer pill label */}
        {beVisible && (
          <>
            <line
              x1={beX}
              x2={beX}
              y1={beY}
              y2={h - padBottom}
              stroke="#10B981"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <circle cx={beX} cy={beY} r="8" fill="#10B981" stroke="white" strokeWidth="3" />
            <rect
              x={beLabelX}
              y={beLabelY}
              rx="6"
              width={beLabelW}
              height="24"
              fill="#10B981"
            />
            <text
              x={beLabelX + 10}
              y={beLabelY + 16}
              fontSize="12.5"
              fill="white"
              fontWeight="700"
              letterSpacing="0.02em"
            >
              {beLabelText}
            </text>
          </>
        )}

        {/* Final-year highlight — bigger circle + larger green-on-navy pill */}
        <circle
          cx={xFor(lastYear)}
          cy={yFor(finalVal)}
          r="8"
          fill="#0B132B"
          stroke="white"
          strokeWidth="3"
        />
        <rect
          x={finalLabelX}
          y={finalLabelY}
          rx="6"
          width={finalLabelW}
          height="26"
          fill="#0B132B"
        />
        <text
          x={finalLabelX + 11}
          y={finalLabelY + 18}
          fontSize="13"
          fill="#10B981"
          fontWeight="800"
          letterSpacing="0.02em"
        >
          {finalLabelText}
        </text>

        {/* X-axis ticks — larger */}
        {tickYears.map((y) => (
          <text
            key={y}
            x={xFor(y)}
            y={h - 14}
            textAnchor="middle"
            fontSize="13"
            fill="#475569"
            fontWeight="700"
          >
            {`Year ${y}`}
          </text>
        ))}
      </svg>
    </div>
  );
}
